import { Outlet, useLocation, useNavigate } from "react-router-dom";
import React, { useState, useEffect, useRef } from "react";
import NotificationDropdown from "../NotificationDropdown";
import { useNotifications } from "../../contexts/NotificationContext";
import { useRankingContext } from "../../contexts/RankingContext";
import { useCredits } from "../../contexts/CreditContext";
import { Progress } from "../ui/progress";
import { getStageMeta, formatElapsed } from "../../lib/rankingStages";
import {
    LayoutDashboard,
    Upload,
    FolderOpen,
    Library,
    Inbox,
    CreditCard,
    Wallet,
    User,
    Menu,
    ChevronsLeft,
    ChevronsRight,
    LogOut,
    Bell,
    Settings,
    ChevronRight,
    Plus,
    Shield,
    Globe,
} from "lucide-react";
import { createPortal } from "react-dom";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { useAuth } from "../../contexts/AuthContext";
import { ROLE_LABELS } from "../../lib/roles";
import BrandLockup from "../BrandLockup";
import { useIsMobile } from "../../hooks/useIsMobile";
import { pageHeading } from "../../lib/pageMeta";

/* ─── TalentFora sidebar palette ─────────────────────────────────────────── */
const S = {
    // sidebar (dark teal)
    sidebarBg:        "#16313A",  // tf-ocean-footer
    sidebarBgSubtle:  "#1B3A42",  // tf-ocean-announce
    sidebarBorder:    "rgba(31,125,145,0.22)",
    sidebarText:      "rgba(255,255,255,0.78)",
    sidebarTextBright:"#FFFFFF",
    sidebarMuted:     "rgba(255,255,255,0.45)",
    sidebarHover:     "rgba(31,125,145,0.16)",
    activeGold:       "#E3704F",  // coral — active text + edge marker
    gold:             "#E3704F",  // alias kept for internal refs
    goldBright:       "#D0573A",  // coral hover
    goldSoft:         "rgba(227,112,79,0.15)",
    // main shell
    mainBg:           "#FBFBF9",
    topBarBg:         "#FFFFFF",
    topBorder:        "#E6E5E1",
    topText:          "#18211C",
    topMuted:         "#5D635F",
    // general refs
    dark:             "#18211C",
    muted:            "#5D635F",
    crimson:          "#1F7D91",  // legacy key — now ocean
    // nav icon colors
    iconDashboard:    "#E3704F",  // coral
    iconUpload:       "#3FBF7F",  // success green
    iconSessions:     "#1F7D91",  // ocean
    iconBilling:      "#3FBF7F",  // success green
    iconWallet:       "#E3704F",  // coral
    iconLibrary:      "#1F7D91",  // ocean
    iconMailbox:      "#E3704F",  // coral
    iconProfile:      "#1F7D91",  // ocean
    iconAdmin:        "#E3704F",
    iconGlobalAdmin:  "#2A5A66",
};

/* ─── Page title map for top-bar breadcrumb ──────────────────────────────── */
// The header label for each route now comes from lib/pageMeta.js, which also owns
// the document title. They used to be two hand-maintained lists and had already
// begun to drift; sharing one map means a renamed page can't say one thing in the
// header and another in the browser tab.

/* ─── Avatar Dropdown ─────────────────────────────────────────────────────── */
const A_MENU = {
    bg: "#FFFFFF", border: "#E6E5E1", surface: "#EEF2F2",
    crimson: "#E3704F", gold: "#1F7D91", dark: "#18211C",
    muted: "#5D635F", font: "'Plus Jakarta Sans', system-ui, sans-serif",
};

function AvatarDropdown({ anchorRef, user, getInitials, onNavigate, onLogout }) {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const width = Math.min(230, window.innerWidth - 16);
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
    const style = {
        position: "fixed",
        bottom: window.innerHeight - rect.top + 8,
        left,
        width,
        background: A_MENU.bg,
        border: `1px solid ${A_MENU.border}`,
        borderRadius: 12,
        boxShadow: "0 -4px 24px rgba(28,16,8,0.14), 0 2px 8px rgba(0,0,0,0.06)",
        zIndex: 1050,
        overflow: "hidden",
    };
    const menuItems = [
        { icon: User,     label: "Profile",  path: "/profile"  },
        { icon: Settings, label: "Settings", path: "/settings" },
    ];
    return (
        <div style={style}>
            {/* User info header */}
            <div style={{ padding: "14px 16px 12px", borderBottom: `1px solid ${A_MENU.border}`, background: A_MENU.surface }}>
                <p style={{ fontSize: 9, letterSpacing: "0.18em", color: A_MENU.muted, textTransform: "uppercase", marginBottom: 8 }}>
                    ACCOUNT
                </p>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Avatar style={{ width: 36, height: 36, flexShrink: 0 }}>
                        <AvatarImage src={user?.profile_picture || user?.avatar} />
                        <AvatarFallback style={{
                            background: A_MENU.gold, color: A_MENU.dark,
                            fontWeight: 700, fontSize: "0.82rem",
                        }}>{getInitials()}</AvatarFallback>
                    </Avatar>
                    <div style={{ overflow: "hidden" }}>
                        <p style={{
                            fontFamily: A_MENU.font, fontSize: 13, fontWeight: 700,
                            color: A_MENU.dark, margin: 0,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
                        }}>
                            {user?.full_name || user?.username || "Operative"}
                        </p>
                        <p style={{
                            fontSize: 10, color: A_MENU.muted, margin: 0,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
                        }}>
                            {user?.email || "—"}
                        </p>
                    </div>
                </div>
                <div style={{ marginTop: 8 }}>
                    <span style={{
                        fontSize: 9, fontWeight: 700, letterSpacing: "0.14em",
                        background: A_MENU.gold + "22", color: A_MENU.gold,
                        border: `1px solid ${A_MENU.gold}44`,
                        borderRadius: 4, padding: "2px 8px", textTransform: "uppercase"
                    }}>
                        {/* Was a binary admin/USER check, which labelled the platform
                            admin as "USER". Driven off the shared role labels now. */}
                        {(ROLE_LABELS[user?.role] || "User").toUpperCase()}
                    </span>
                </div>
            </div>

            {/* Nav items */}
            <div style={{ padding: "6px 8px" }}>
                {menuItems.map(({ icon: Icon, label, path }) => (
                    <button
                        key={path}
                        onClick={() => onNavigate(path)}
                        style={{
                            width: "100%", display: "flex", alignItems: "center", gap: 10,
                            padding: "9px 10px", borderRadius: 8, border: "none",
                            background: "transparent", color: A_MENU.dark, cursor: "pointer",
                            fontSize: 13, fontWeight: 500, fontFamily: "inherit",
                            transition: "background 0.15s", textAlign: "left",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = A_MENU.surface; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                    >
                        <Icon size={14} style={{ color: A_MENU.gold, flexShrink: 0 }} />
                        {label}
                    </button>
                ))}
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: A_MENU.border, margin: "0 8px" }} />

            {/* Logout */}
            <div style={{ padding: "6px 8px 8px" }}>
                <button
                    onClick={onLogout}
                    style={{
                        width: "100%", display: "flex", alignItems: "center", gap: 10,
                        padding: "9px 10px", borderRadius: 8, border: "none",
                        background: "transparent", color: A_MENU.crimson, cursor: "pointer",
                        fontSize: 13, fontWeight: 600, fontFamily: "inherit",
                        transition: "background 0.15s", textAlign: "left",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = A_MENU.crimson + "12"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                >
                    <LogOut size={14} style={{ flexShrink: 0 }} />
                    Sign Out
                </button>
            </div>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
export default function RootLayout({ pushToast }) {
    const location   = useLocation();
    const navigate   = useNavigate();
    const isMobile   = useIsMobile();
    const { user, authenticated, isAdmin, isGlobalAdmin, logout } = useAuth();
    const [sidebarOpen, setSidebarOpen]   = useState(() => {
        if (typeof window === "undefined") return true;
        return window.innerWidth >= 768;
    });
    const [isNotifOpen, setIsNotifOpen]   = useState(false);
    const [avatarOpen, setAvatarOpen]     = useState(false);

    // Close drawer on route change when on mobile
    useEffect(() => {
        if (isMobile) setSidebarOpen(false);
    }, [location.pathname, isMobile]);

    // When crossing the mobile breakpoint, restore desktop sidebar state
    useEffect(() => {
        if (!isMobile) setSidebarOpen(true);
    }, [isMobile]);
    const notifBtnRef  = useRef(null);
    const avatarBtnRef = useRef(null);
    const { unreadCount } = useNotifications();
    const { isRanking, rankingProgress, rankingStage, rankingElapsedSec } = useRankingContext();
    const { balance } = useCredits();
    const creditBalance = balance?.total || 0;

    const allNav = [
        { name: "Dashboard",      href: "/dashboard", icon: LayoutDashboard, color: S.iconDashboard, hideForGA: true },
        { name: "Upload Resumes", href: "/upload",    icon: Upload,           color: S.iconUpload,    hideForGA: true },
        { name: "Sessions",       href: "/sessions",  icon: FolderOpen,       color: S.iconSessions,  hideForGA: true },
        { name: "Resume Library", href: "/library",   icon: Library,          color: S.iconLibrary,   hideForGA: true },
        { name: "Mailboxes",      href: "/mailbox",   icon: Inbox,            color: S.iconMailbox,   hideForGA: true },
        { name: "Billing",        href: "/billing",   icon: CreditCard,       color: S.iconBilling },
        { name: "Wallet",         href: "/wallet",    icon: Wallet,           color: S.iconWallet },
        { name: "Profile",        href: "/profile",   icon: User,             color: S.iconProfile },
        ...(isGlobalAdmin() ? [{ name: "Global Admin", href: "/global-admin", icon: Globe, color: S.iconGlobalAdmin }] : []),
    ];
    const navigation = isGlobalAdmin()
        ? allNav.filter(i => !i.hideForGA)
        : allNav;

    const isActive = (p) => location.pathname === p || location.pathname.startsWith(p + "/");

    const handleLogout = async () => {
        try { await logout(); } catch {}
        pushToast?.({ title: "Logged Out", message: "You have been successfully logged out", type: "success" });
        navigate("/loginNew");
    };

    const getInitials = () => {
        const name = user?.full_name || user?.fullName || user?.name;
        if (!name) return "U";
        const parts = name.split(" ");
        return parts.length >= 2
            ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
            : name.slice(0, 2).toUpperCase();
    };

    const getFirstName = () => {
        const name = user?.full_name || user?.fullName || user?.name || user?.username;
        return name ? name.split(" ")[0] : "User";
    };

    const pageTitle = pageHeading(location.pathname);

    /* ── Sidebar nav item — gold left-edge marker on active, constant icon color ── */
    const NavItem = ({ item }) => {
        const Icon       = item.icon;
        const active     = isActive(item.href);
        const iconColor  = item.color || S.gold;
        return (
            <button
                onClick={() => navigate(item.href)}
                title={!sidebarOpen ? item.name : undefined}
                style={{
                    position: "relative",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: sidebarOpen ? "0.62rem 0.9rem 0.62rem 1rem" : "0.62rem",
                    justifyContent: sidebarOpen ? "flex-start" : "center",
                    borderRadius: "8px",
                    border: "none",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: "0.92rem",
                    fontWeight: active ? 600 : 500,
                    letterSpacing: "0.005em",
                    transition: "background-color 180ms, color 180ms",
                    backgroundColor: active ? S.goldSoft : "transparent",
                    color: active ? S.activeGold : S.sidebarText,
                    marginBottom: "3px",
                    textAlign: "left",
                    overflow: "hidden",
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.backgroundColor = S.sidebarHover; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.backgroundColor = "transparent"; }}
            >
                {/* gold edge marker — like a page tab on an archive ledger */}
                {active && sidebarOpen && (
                    <span style={{
                        position: "absolute",
                        left: 0, top: "20%", bottom: "20%",
                        width: "3px",
                        backgroundColor: S.activeGold,
                        borderRadius: "0 2px 2px 0",
                    }} />
                )}
                <Icon size={19} style={{ flexShrink: 0, color: iconColor, strokeWidth: 2 }} />
                {sidebarOpen && (
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                        {item.name}
                    </span>
                )}
                {sidebarOpen && active && (
                    <ChevronRight size={14} style={{ color: S.activeGold, opacity: 0.7, flexShrink: 0 }} />
                )}
            </button>
        );
    };

    /* ─────────────────────────────────────────────────────────────────── */
    // On mobile the sidebar becomes a slide-in drawer that always renders at full 260 px
    // when open and is translated off-screen when closed.
    const sidebarVisualWidth = isMobile ? 260 : (sidebarOpen ? 260 : 76);
    const sidebarTransform = isMobile
        ? (sidebarOpen ? "translateX(0)" : "translateX(-100%)")
        : "none";

    return (
        <div style={{ display: "flex", minHeight: "100vh", backgroundColor: S.mainBg }}>

            {/* ── Mobile backdrop ───────────────────────────────────────── */}
            {isMobile && sidebarOpen && (
                <div
                    onClick={() => setSidebarOpen(false)}
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 35,
                        backgroundColor: "rgba(24, 33, 28, 0.55)",
                        backdropFilter: "blur(2px)",
                    }}
                />
            )}

            {/* ── SIDEBAR ──────────────────────────────────────────────── */}
            <aside style={{
                position: "fixed",
                left: 0,
                top: 0,
                zIndex: 40,
                height: "100vh",
                width: `${sidebarVisualWidth}px`,
                transform: sidebarTransform,
                backgroundColor: S.sidebarBg,
                borderRight: `1px solid ${S.sidebarBorder}`,
                display: "flex",
                flexDirection: "column",
                transition: "width 280ms cubic-bezier(.4,0,.2,1), transform 280ms cubic-bezier(.4,0,.2,1)",
                overflow: "hidden",
                boxShadow: "1px 0 0 rgba(31,125,145,0.06), 2px 0 24px rgba(0,0,0,0.22)",
            }}>
                {/* Brand header — expanded: logo + wordmark on the left, collapse chevron on
                    the right. Collapsed: the 76px rail is too narrow for both, so it shows only
                    the expand chevron, centered. The brand mark returns when expanded. */}
                <div style={{
                    padding: sidebarOpen ? "0 1.1rem" : "0",
                    height: "76px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: sidebarOpen ? "space-between" : "center",
                    borderBottom: `1px solid ${S.sidebarBorder}`,
                    flexShrink: 0,
                }}>
                    {sidebarOpen && (
                        <div
                            onClick={() => navigate("/dashboard")}
                            style={{ display: "flex", alignItems: "center", cursor: "pointer", flex: 1, minWidth: 0, marginRight: 8 }}
                        >
                            {/* Lockup is ~5.86× as wide as it is tall; at height 28 it comes to
                                ~164px, which clears the 260px sidebar beside the collapse
                                chevron without clipping. */}
                            <BrandLockup height={28} tone="dark" />
                        </div>
                    )}

                    <button
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
                        title={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
                        style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: S.gold,
                            padding: "6px",
                            borderRadius: "6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            transition: "background-color 160ms, color 160ms",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.backgroundColor = S.sidebarHover; e.currentTarget.style.color = S.goldBright; }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = S.gold; }}
                    >
                        {sidebarOpen
                            ? <ChevronsLeft size={20} strokeWidth={2.25} />
                            : <ChevronsRight size={20} strokeWidth={2.25} />}
                    </button>
                </div>

                {/* "+ New Entry" — gold-outlined ghost button (echoes the image) */}
                <div style={{ padding: sidebarOpen ? "1.1rem 1rem 0.4rem" : "1.1rem 0.6rem 0.4rem", flexShrink: 0 }}>
                    <button
                        onClick={() => navigate("/upload")}
                        title={!sidebarOpen ? "New Screening" : undefined}
                        style={{
                            width: "100%",
                            backgroundColor: S.gold,
                            color: "#FFFFFF",
                            border: "none",
                            borderRadius: "8px",
                            padding: sidebarOpen ? "0.7rem 1rem" : "0.7rem",
                            fontWeight: 600,
                            fontSize: "0.92rem",
                            letterSpacing: "0.01em",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            fontFamily: "inherit",
                            transition: "background-color 200ms, opacity 200ms",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.backgroundColor = S.goldBright; }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = S.gold; }}
                    >
                        <Plus size={18} strokeWidth={2.25} />
                        {sidebarOpen && "New Screening"}
                    </button>
                </div>

                {/* Navigation */}
                <nav style={{ flex: 1, padding: "0.4rem 0.85rem 0.85rem", overflowY: "auto" }}>
                    {navigation.map(item => <NavItem key={item.href} item={item} />)}

                    {/* Admin link (conditional) */}
                    {isAdmin() && !isGlobalAdmin() && (
                        <NavItem item={{ name: "Admin", href: "/admin", icon: Shield, color: S.iconAdmin }} />
                    )}
                </nav>

                {/* Avatar trigger at bottom — gold circle, cream name, muted email */}
                <div style={{
                    padding: "0.9rem 0.85rem",
                    borderTop: `1px solid ${S.sidebarBorder}`,
                    flexShrink: 0,
                    boxShadow: "inset 0 1px 0 rgba(245,234,208,0.03)",
                }}>
                    <button
                        ref={avatarBtnRef}
                        onClick={() => setAvatarOpen(v => !v)}
                        style={{
                            width: "100%", display: "flex", alignItems: "center", gap: "12px",
                            padding: "0.45rem 0.55rem", borderRadius: "10px", border: "none",
                            cursor: "pointer", backgroundColor: avatarOpen ? S.sidebarHover : "transparent",
                            fontFamily: "inherit", justifyContent: sidebarOpen ? "flex-start" : "center",
                            transition: "background-color 180ms",
                        }}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = S.sidebarHover)}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = avatarOpen ? S.sidebarHover : "transparent")}
                    >
                        <Avatar style={{
                            width: "38px", height: "38px", flexShrink: 0,
                            boxShadow: `0 0 0 1px rgba(227,112,79,0.36)`,
                        }}>
                            <AvatarImage src={user?.profile_picture || user?.avatar} />
                            <AvatarFallback style={{
                                backgroundColor: S.gold, color: "#fff",
                                fontWeight: 700, fontSize: "0.86rem",
                                letterSpacing: "0.02em",
                            }}>
                                {getInitials()}
                            </AvatarFallback>
                        </Avatar>
                        {sidebarOpen && (
                            <div style={{ flex: 1, textAlign: "left", overflow: "hidden" }}>
                                <p style={{ fontSize: "0.92rem", fontWeight: 600, color: S.sidebarTextBright, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {getFirstName()}
                                </p>
                                <p style={{ fontSize: "0.72rem", color: S.sidebarMuted, margin: "1px 0 0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {user?.email || "Operative"}
                                </p>
                            </div>
                        )}
                    </button>
                </div>
            </aside>

            {/* Avatar dropdown portal */}
            {avatarOpen && createPortal(
                <>
                    <div style={{ position: "fixed", inset: 0, zIndex: 1040 }} onClick={() => setAvatarOpen(false)} />
                    <AvatarDropdown
                        anchorRef={avatarBtnRef}
                        user={user}
                        getInitials={getInitials}
                        onNavigate={(path) => { setAvatarOpen(false); navigate(path); }}
                        onLogout={() => { setAvatarOpen(false); handleLogout(); }}
                    />
                </>,
                document.body
            )}

            {/* ── MAIN CONTENT ─────────────────────────────────────────── */}
            <div style={{
                flex: 1,
                marginLeft: isMobile ? 0 : (sidebarOpen ? "260px" : "76px"),
                transition: "margin-left 280ms cubic-bezier(.4,0,.2,1)",
                display: "flex",
                flexDirection: "column",
                minHeight: "100vh",
                backgroundColor: S.mainBg,
                minWidth: 0,
            }}>
                {/* Top Bar — dark ink to match sidebar; gold accents on actions */}
                <header style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 30,
                    height: isMobile ? "64px" : "76px",
                    borderBottom: `1px solid ${S.topBorder}`,
                    backgroundColor: S.topBarBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: isMobile ? "0 0.85rem" : "0 1.9rem",
                    boxShadow: "0 1px 0 rgba(24,33,28,0.04)",
                    gap: 8,
                }}>
                    {/* Page title — refined editorial header */}
                    <div style={{ display: "flex", alignItems: "center", gap: isMobile ? "8px" : "12px", minWidth: 0 }}>
                        {isMobile && (
                            <button
                                onClick={() => setSidebarOpen(o => !o)}
                                aria-label="Open menu"
                                style={{
                                    background: "#FFFFFF",
                                    border: `1px solid ${S.topBorder}`,
                                    borderRadius: 8,
                                    color: S.topMuted,
                                    cursor: "pointer",
                                    width: 38,
                                    height: 38,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    transition: "border-color 180ms, color 180ms",
                                }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor = S.crimson; e.currentTarget.style.color = S.crimson; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = S.topBorder; e.currentTarget.style.color = S.topMuted; }}
                            >
                                <Menu size={18} strokeWidth={2.25} />
                            </button>
                        )}
                        <span style={{
                            width: "3px",
                            height: "22px",
                            backgroundColor: S.crimson,
                            borderRadius: "2px",
                            flexShrink: 0,
                        }} />
                        <span style={{
                            fontSize: isMobile ? "1.05rem" : "1.32rem",
                            fontWeight: 600,
                            color: S.topText,
                            fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                            letterSpacing: "0.005em",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}>
                            {pageTitle}
                        </span>
                    </div>

                    {/* Right side actions */}
                    <div style={{ display: "flex", alignItems: "center", gap: isMobile ? "0.4rem" : "0.7rem" }}>
                        {/* Process Record CTA (desktop only — reachable via sidebar on mobile; hidden on the upload page since it only links there) */}
                        {!isMobile && !isActive("/upload") && (
                            <button
                                onClick={() => navigate("/upload")}
                                style={{
                                    padding: "0.5rem 1.15rem",
                                    border: `1px solid ${S.topBorder}`,
                                    borderRadius: "8px",
                                    backgroundColor: "transparent",
                                    color: S.topText,
                                    fontWeight: 600,
                                    fontSize: "0.84rem",
                                    cursor: "pointer",
                                    fontFamily: "inherit",
                                    letterSpacing: "0.04em",
                                    transition: "background-color 200ms, border-color 200ms, color 200ms",
                                }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor = S.crimson; e.currentTarget.style.color = S.crimson; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = S.topBorder; e.currentTarget.style.color = S.topText; }}
                            >
                                Process Record
                            </button>
                        )}

                        {/* Admin badges (desktop only) */}
                        {!isMobile && isAdmin() && !isGlobalAdmin() && (
                            <button
                                onClick={() => navigate("/admin")}
                                style={{ padding: "0.42rem 0.85rem", border: `1px solid ${S.gold}`, borderRadius: "8px", backgroundColor: "rgba(227,112,79,0.12)", color: S.gold, fontWeight: 600, fontSize: "0.78rem", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: "6px" }}
                            >
                                <Shield size={13} /> Admin
                            </button>
                        )}
                        {!isMobile && isGlobalAdmin() && (
                            <button
                                onClick={() => navigate("/global-admin")}
                                style={{ padding: "0.42rem 0.85rem", border: `1px solid #2A5A66`, borderRadius: "8px", backgroundColor: "rgba(42,90,102,0.14)", color: "#1F7D91", fontWeight: 600, fontSize: "0.78rem", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: "6px" }}
                            >
                                <Globe size={13} /> Global Admin
                            </button>
                        )}

                        {/* Credits pill */}
                        <button
                            onClick={() => navigate("/wallet")}
                            style={{
                                padding: isMobile ? "0.35rem 0.7rem" : "0.45rem 0.95rem",
                                border: `1px solid ${S.topBorder}`,
                                borderRadius: "999px",
                                backgroundColor: "#FFFFFF",
                                color: S.topText,
                                fontWeight: 600,
                                fontSize: isMobile ? "0.72rem" : "0.8rem",
                                cursor: "pointer",
                                fontFamily: "inherit",
                                display: "flex",
                                alignItems: "center",
                                gap: "7px",
                                transition: "border-color 180ms",
                                whiteSpace: "nowrap",
                            }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = S.crimson; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = S.topBorder; }}
                        >
                            <Wallet size={isMobile ? 12 : 14} style={{ color: S.crimson }} />
                            {creditBalance}{!isMobile && " Credits"}
                        </button>

                        {/* Notifications */}
                        <div style={{ position: "relative" }}>
                            <button
                                ref={notifBtnRef}
                                onClick={() => setIsNotifOpen(!isNotifOpen)}
                                style={{
                                    width: "38px",
                                    height: "38px",
                                    border: `1px solid ${S.topBorder}`,
                                    borderRadius: "8px",
                                    backgroundColor: "#FFFFFF",
                                    color: S.topMuted,
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    position: "relative",
                                    transition: "border-color 180ms, color 180ms",
                                }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor = S.crimson; e.currentTarget.style.color = S.crimson; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = S.topBorder; e.currentTarget.style.color = S.topMuted; }}
                            >
                                <Bell size={16} />
                                {unreadCount > 0 && (
                                    <span style={{
                                        position: "absolute",
                                        top: "6px",
                                        right: "6px",
                                        width: "8px",
                                        height: "8px",
                                        borderRadius: "50%",
                                        backgroundColor: S.gold,
                                        boxShadow: `0 0 0 2px ${S.topBarBg}`,
                                    }} />
                                )}
                            </button>
                            <NotificationDropdown
                                isOpen={isNotifOpen}
                                onClose={() => setIsNotifOpen(false)}
                                anchorRef={notifBtnRef}
                            />
                        </div>

                        {/* Settings */}
                        <button
                            onClick={() => navigate("/settings")}
                            style={{
                                width: "38px",
                                height: "38px",
                                border: `1px solid ${S.topBorder}`,
                                borderRadius: "8px",
                                backgroundColor: "#FFFFFF",
                                color: S.topMuted,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "border-color 180ms, color 180ms",
                            }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = S.crimson; e.currentTarget.style.color = S.crimson; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = S.topBorder; e.currentTarget.style.color = S.topMuted; }}
                        >
                            <Settings size={16} />
                        </button>
                    </div>
                </header>

                {/* Ranking progress strip */}
                {isRanking && (
                    <div style={{
                        margin: "0.75rem 1.75rem 0",
                        borderRadius: "10px",
                        overflow: "hidden",
                        border: `1px solid rgba(31,125,145,0.3)`,
                        backgroundColor: "rgba(31,125,145,0.06)",
                    }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.5rem 1rem" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.8rem", fontWeight: 600, color: "#1F7D91" }}>
                                <span style={{ width: "7px", height: "7px", borderRadius: "50%", backgroundColor: "#1F7D91", animation: "pulse 1.5s infinite" }} />
                                {getStageMeta(rankingStage).paused ? "Ranking paused" : "AI Ranking in Progress"}
                                {rankingStage && !getStageMeta(rankingStage).paused && (
                                    <span style={{ color: "#5D635F", fontWeight: 400 }}>· {getStageMeta(rankingStage).title}</span>
                                )}
                            </div>
                            <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "#1F7D91", fontFamily: "monospace" }}>
                                {formatElapsed(rankingElapsedSec)}
                            </span>
                        </div>
                        <Progress
                            value={rankingProgress}
                            shimmer={!getStageMeta(rankingStage).paused}
                            className="h-1 rounded-none"
                            style={{ backgroundColor: "#E6E5E1" }}
                        />
                    </div>
                )}

                {/* Page content */}
                <main style={{
                    padding: ["/dashboard", "/upload"].includes(location.pathname)
                        ? "0"
                        : (isMobile ? "1rem" : "1.75rem"),
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 0,
                }}>
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
